import React, { useEffect, useState, useCallback } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { ArrowRight, CheckCircle2, Sparkles, Award, Lock, Clock } from "lucide-react";
import { useAuth } from "@/lib/auth";
import { toast } from "sonner";
import { useSeo } from "@/hooks/useSeo";

// Slug → key mapping (slugs are SEO-friendly)
const SLUG_TO_KEY = {
  "canada-citizenship": "canada_citizenship",
  "alberta-class-7":    "alberta_class7",
  "ontario-g1":         "ontario_g1",
};

// Per-test SEO copy — hand-tuned for Canadian search intent
const SEO_BY_SLUG = {
  "canada-citizenship": {
    title: "Canada Citizenship Practice Test — Free Mock Exam (2026) | TeachMeOnline.ca",
    description: "Free unlimited Canada Citizenship practice test. 150+ questions based on Discover Canada — rights, history, government, geography. Instant scoring, AI explanations, no credit card.",
    keywords: "canada citizenship practice test, canadian citizenship mock exam, discover canada quiz, citizenship test 2026",
  },
  "alberta-class-7": {
    title: "Alberta Class 7 Practice Test — Free Learner Permit Quiz (2026) | TeachMeOnline.ca",
    description: "Free Alberta Class 7 knowledge test practice. Road signs, rules of the road, parking, school zones, safety. Unlimited attempts, instant scoring, AI-powered explanations.",
    keywords: "alberta class 7 practice test, alberta learner practice test, alberta road signs test, class 7 knowledge test 2026",
  },
  "ontario-g1": {
    title: "Ontario G1 Practice Test — Free Mock Exam (2026) | TeachMeOnline.ca",
    description: "Free Ontario G1 practice test. Road signs, traffic laws, safe driving, highway rules. Unlimited practice, instant scoring, AI explanations. Pass your G1 with confidence.",
    keywords: "ontario g1 practice test, ontario g1 mock exam, ontario road signs test, g1 knowledge test 2026",
  },
};

export default function PracticeTestDetail() {
  const { slug } = useParams();
  const nav = useNavigate();
  const { user } = useAuth();
  const [test, setTest] = useState(null);
  const [preview, setPreview] = useState([]);
  const [busy, setBusy] = useState("");
  const [lang, setLang] = useState(() => localStorage.getItem("practice_lang") || "");
  const [langs, setLangs] = useState({});

  const testKey = SLUG_TO_KEY[slug];
  const seoMeta = SEO_BY_SLUG[slug] || {};

  useSeo({
    title: seoMeta.title,
    description: seoMeta.description,
    canonical: slug ? `https://teacher4me.ca/practice/${slug}` : undefined,
    image: "https://teacher4me.ca/tmo-logo.png",
    jsonLd: test ? {
      "@context": "https://schema.org",
      "@type": "Quiz",
      "name": test.name,
      "description": seoMeta.description || test.blurb,
      "url": `https://teacher4me.ca/practice/${slug}`,
      "educationalUse": "practice",
      "isAccessibleForFree": true,
      "inLanguage": "en-CA",
      "provider": {
        "@type": "Organization",
        "name": "TeachMeOnline.ca",
        "url": "https://teacher4me.ca",
      },
      "about": test.categories.map((c) => ({ "@type": "Thing", "name": c })),
      "hasPart": preview.slice(0, 5).map((q) => ({
        "@type": "Question",
        "name": q.question,
        "acceptedAnswer": { "@type": "Answer", "text": q.options[q.correct_index] },
      })),
    } : undefined,
  });

  const load = useCallback(async () => {
    if (!testKey) return;
    try {
      const [a, b, c] = await Promise.all([
        api.get(`/api/practice/tests/${testKey}`),
        api.get(`/api/practice/tests/${testKey}/preview${lang ? `?lang=${lang}` : ""}`),
        api.get(`/api/practice/tests`),
      ]);
      setTest(a.data);
      setPreview(b.data.questions || []);
      setLangs(c.data.languages || {});
    } catch (e) { toast.error("Could not load test"); }
  }, [testKey, lang]);

  useEffect(() => { load(); }, [load]);

  const pickLang = (v) => {
    setLang(v);
    localStorage.setItem("practice_lang", v);
  };

  const startTest = async (mode) => {
    if (!user) { nav(`/register?next=/practice/${slug}`); return; }
    setBusy(mode);
    try {
      const { data } = await api.post(`/api/practice/tests/${testKey}/start`,
        { mode, lang: lang || null });
      nav(`/practice/${slug}/play/${data.attempt_id}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not start test");
    } finally { setBusy(""); }
  };

  if (!testKey) return <div className="p-12 text-center">Unknown test</div>;
  if (!test) return <div className="p-12 text-center text-[#4B5563]">Loading…</div>;

  return (
    <div data-testid="practice-detail">
      <section className="bg-gradient-to-b from-[#FEF2F2] to-white">
        <div className="max-w-5xl mx-auto px-6 py-12">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-[#064E3B] text-white text-xs font-bold uppercase tracking-widest">
            <Sparkles className="w-3 h-3" /> FREE · Unlimited Practice
          </div>
          <h1 className="font-display font-black text-3xl md:text-5xl mt-3 leading-tight">{test.name}</h1>
          <p className="text-[#4B5563] mt-3 max-w-2xl">{test.blurb}</p>
          <div className="mt-5 grid sm:grid-cols-4 gap-3 max-w-2xl">
            <Stat label="Questions" value={test.question_count} />
            <Stat label="Categories" value={test.categories.length} />
            <Stat label="To pass" value={`${test.passing_score}%`} />
            <Stat label="Official test" value={`${test.official_test_questions} Qs`} />
          </div>
        </div>
      </section>

      {/* MODE PICKER */}
      <section className="max-w-5xl mx-auto px-6 py-10">
        <div className="flex items-center justify-between flex-wrap gap-3 mb-5">
          <h2 className="font-display font-black text-2xl">Pick your practice mode</h2>
          {Object.keys(langs).length > 0 && (
            <div className="inline-flex items-center gap-2" data-testid="lang-picker">
              <span className="text-xs text-[#4B5563] font-semibold">Language:</span>
              <button data-testid="lang-en" onClick={() => pickLang("")}
                className={`text-xs px-3 py-1.5 rounded-full font-bold transition-colors ${lang === "" ? "bg-[#064E3B] text-white" : "bg-[#F3F4F6] text-[#4B5563] hover:bg-[#E5E7EB]"}`}>
                English
              </button>
              {Object.entries(langs).map(([code, l]) => (
                <button key={code} data-testid={`lang-${code}`} onClick={() => pickLang(code)}
                  className={`text-xs px-3 py-1.5 rounded-full font-bold transition-colors ${lang === code ? "bg-[#D92D20] text-white" : "bg-[#F3F4F6] text-[#4B5563] hover:bg-[#E5E7EB]"}`}>
                  {l.native}
                </button>
              ))}
            </div>
          )}
        </div>
        <div className="mt-5 grid md:grid-cols-3 gap-4">
          {Object.entries(test.modes).map(([key, mode]) => (
            <button key={key} data-testid={`mode-${key}`} onClick={() => startTest(key)}
              disabled={busy === key || test.question_count < 5}
              className="text-left bg-white border-2 border-[#E5E7EB] rounded-2xl p-5 hover:border-[#D92D20] transition-colors disabled:opacity-50">
              <div className="flex items-center gap-2">
                <Clock className="w-4 h-4 text-[#D92D20]" />
                <span className="font-display font-bold">{mode.name}</span>
              </div>
              <div className="font-display font-black text-3xl mt-2">{mode.count}</div>
              <div className="text-xs uppercase tracking-widest text-[#4B5563] -mt-1">questions</div>
              <div className="text-xs text-[#4B5563] mt-2">~{mode.time_limit_min} min · randomized from {test.question_count} Q pool</div>
              <div className="mt-3 text-sm font-semibold text-[#D92D20] inline-flex items-center gap-1">
                {busy === key ? "Starting…" : (user ? "Start" : "Sign up & start")} <ArrowRight className="w-3.5 h-3.5" />
              </div>
            </button>
          ))}
        </div>
        {!user && (
          <p className="text-xs text-[#4B5563] mt-3 inline-flex items-center gap-1">
            <Lock className="w-3 h-3" /> A free account is required to start a test (registration takes 30 seconds).
          </p>
        )}
        {test.question_count < 5 && (
          <div className="mt-4 bg-[#FEF3C7] text-[#92400E] text-sm rounded-xl p-4">
            Our question bank is still being prepared for this test. Check back in a few minutes!
          </div>
        )}
      </section>

      {/* SAMPLE QUESTIONS (SEO + conversion) */}
      <section className="bg-[#F9FAFB] border-y border-[#E5E7EB]">
        <div className="max-w-5xl mx-auto px-6 py-10">
          <div className="label-eyebrow">Try a sample</div>
          <h2 className="font-display font-black text-2xl mt-1">5 free sample questions — no signup needed.</h2>
          <div className="mt-6 space-y-5">
            {preview.length === 0 && <div className="text-sm text-[#4B5563]">Sample questions appear once seeding completes.</div>}
            {preview.map((q, i) => (
              <PreviewQ key={q.id} q={q} idx={i + 1} />
            ))}
          </div>
        </div>
      </section>

      {/* CATEGORIES */}
      <section className="max-w-5xl mx-auto px-6 py-10">
        <h2 className="font-display font-black text-2xl">Topics covered</h2>
        <div className="mt-4 flex flex-wrap gap-2">
          {test.categories.map((c) => (
            <span key={c} className="text-xs px-3 py-1.5 rounded-full bg-[#FEF2F2] text-[#D92D20] font-semibold">{c}</span>
          ))}
        </div>
      </section>

      {/* TUTORING CONVERSION */}
      <section className="bg-[#064E3B] text-white">
        <div className="max-w-3xl mx-auto px-6 py-12 text-center">
          <div className="text-xs uppercase tracking-widest text-white/70 font-bold">Need help studying?</div>
          <h2 className="font-display font-black text-3xl mt-2">Try Teacher4Me AI Tutoring</h2>
          <p className="text-sm text-white/80 mt-2">Unlimited AI tutor · worksheets · weekly parent reports. Free Forever plan available.</p>
          <Link to="/tutor-landing" data-testid="detail-tutoring-cta" className="btn-primary inline-flex items-center gap-2 mt-5">
            Start Free Tutoring <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
      </section>
    </div>
  );
}

function Stat({ label, value }) {
  return (
    <div className="bg-white border border-[#E5E7EB] rounded-xl p-3 text-center">
      <div className="font-display font-black text-2xl">{value}</div>
      <div className="text-[10px] uppercase tracking-widest text-[#4B5563]">{label}</div>
    </div>
  );
}

function PreviewQ({ q, idx }) {
  const [revealed, setRevealed] = useState(false);
  return (
    <div className="bg-white border border-[#E5E7EB] rounded-2xl p-5" data-testid={`preview-q-${idx}`}>
      <div className="text-xs uppercase tracking-widest text-[#D92D20] font-bold">Q{idx} · {q.category}</div>
      <div className="font-display font-bold mt-2">{q.question}</div>
      <div className="mt-3 space-y-2">
        {q.options.map((o, i) => {
          const isCorrect = revealed && i === q.correct_index;
          return (
            <div key={`${q.id}-${i}`} className={`text-sm border rounded-lg px-3 py-2 ${isCorrect ? "border-[#064E3B] bg-[#D1FAE5] font-semibold" : "border-[#E5E7EB]"}`}>
              {String.fromCharCode(65 + i)}. {o}
              {isCorrect && <CheckCircle2 className="w-4 h-4 inline ml-2 text-[#064E3B]" />}
            </div>
          );
        })}
      </div>
      {revealed ? (
        <p className="mt-3 text-sm text-[#4B5563]"><b>Explanation:</b> {q.explanation}</p>
      ) : (
        <button data-testid={`preview-reveal-${idx}`} onClick={() => setRevealed(true)} className="text-sm font-semibold text-[#D92D20] mt-3">Show answer →</button>
      )}
    </div>
  );
}
